/* eslint-disable radix */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react';
import { Button, Input } from 'reactstrap';
import { HiOutlineDownload } from 'react-icons/hi';
import { MdModeEdit } from 'react-icons/md';
import { LuUpload } from 'react-icons/lu';
import { useLazyQuery, useQuery } from '@apollo/client';
import moment from 'moment';
import { toast } from 'react-toastify';
import Loader from 'components/common/Loader';
import ErrorMessage from 'components/ErrorMessage';
import NoDataFound from 'components/NoDataFound';
import ErrorBoundary from 'components/common/ErrorBoundary';
import { FSBTable } from 'components/common/FSBTable';
import { BillingAndPaymentColumns } from 'pages/Vendor/constants';
import { authContext } from 'contexts/AuthContext';
import {
  SUBSCRIPTION_PLAN_BILLING_AND_PAYMENT_QUERY,
  USER_SUBSCRIPTION_PLAN_PAYMENT_INVOICE,
} from 'pages/Vendor/VendorGraphQL';
import FSBModal from 'components/common/FSBModal';
import { axiosPost, axiosUpdate } from 'services';
import VendorInvoiceView from './components/VendorInvoiceView';

const VendorBillingAndPayment = ({ id }) => {
  const [filters, setFilters] = useState({
    filter: [
      {
        key: 'trader',
        value: parseInt(id),
        joinAttributeName: 'userSubscription',
        type: 'INT',
        op: 'EQ',
      },
      {
        key: 'status',
        value: 'SUCCESS',
        type: 'STRING',
        op: 'EQ',
      },
    ],
    pageNo: 1,
    pageSize: 10,
    sortCol: 'createdAt',
    sortDirection: 'DESC',
    pagination: true,
  });
  const [showAddRecord, setAddRecord] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [uploadInvoiceLoader, setUploadInvoiceLoader] = useState(false); // this state swaps between id of the userSubscriptionPayment and false
  const [updatedInvoiceUrls, setUpdatedInvoiceUrls] = useState(null);
  const { auth } = useContext(authContext);
  const [billingAndPaymentColumnState, setBillingAndPaymentColumnState] =
    useState(BillingAndPaymentColumns);

  const drawerToggle = (ID) => {
    setSelectedInvoiceId(ID);
    setAddRecord(!showAddRecord);
  };

  const [userSubscriptionPaymentInvoice] = useLazyQuery(
    USER_SUBSCRIPTION_PLAN_PAYMENT_INVOICE,
  );

  const { data, loading, error } = useQuery(
    SUBSCRIPTION_PLAN_BILLING_AND_PAYMENT_QUERY,
    {
      variables: {
        ...filters,
      },
      fetchPolicy: 'network-only',
    },
  );

  const handlePageChange = ({ pageSize, pageNo }) => {
    setFilters({
      ...filters,
      pageNo:
        data?.userSubscriptionPayments?.totalCount < pageSize ? 1 : pageNo,
      pageSize,
    });
  };

  const onSortChange = (sort) => {
    setFilters({
      ...filters,
      sortCol: sort()[0].id,
      sortDirection: sort()[0].desc ? 'DESC' : 'ASC',
    });
  };

  const sortBy = [
    filters.sortCol === null
      ? { id: null, desc: null }
      : {
          id: filters.sortCol,
          desc: filters.sortDirection === 'DESC',
        },
  ];

  const downloadPDF = (base64String, filename) => {
    const linkSource = `data:application/pdf;base64,${base64String}`;
    const downloadLink = document.createElement('a');
    downloadLink.href = linkSource;
    downloadLink.download = filename;
    downloadLink.click();
  };

  // eslint-disable-next-line no-unused-vars
  const handleDownload = async (invoice) => {
    const { data: invoiceData } = await userSubscriptionPaymentInvoice({
      variables: { id: invoice?.id },
    });
    const base64String = invoiceData?.userSubscriptionPaymentInvoice;
    const filename = `Invoice_${id}_${
      invoice?.userSubscription?.subscriptionPlan?.planName
    }_${moment(invoice?.createdAt).format('D MMM, YYYY')}.pdf`;
    downloadPDF(base64String, filename);
  };

  const updateInoviceUrlForSubscriptionPayment = async (
    subscriptionPaymentId,
    invoiceUrl,
  ) => {
    try {
      const response = await axiosUpdate(
        `${process.env.REACT_APP_REST_API_ENDPOINT}subscription/update-invoice-url?invoiceUrl=${invoiceUrl}&userSubscriptionPaymentId=${subscriptionPaymentId}`,
        {},
        auth?.data?.token,
      );
      if (updatedInvoiceUrls) {
        setUpdatedInvoiceUrls((prev) => {
          const existingIndex = Array.isArray(prev)
            ? prev.findIndex((item) => item.id === subscriptionPaymentId)
            : -1;
          if (existingIndex !== -1) {
            // Update the url for existing item
            const updated = [...prev];
            updated[existingIndex].url = response.data?.data;
            return updated;
          }
          return [
            ...(prev || []),
            { id: subscriptionPaymentId, url: response.data?.data },
          ];
        });
      } else {
        setUpdatedInvoiceUrls([
          { id: subscriptionPaymentId, url: response.data?.data },
        ]);
      }
      toast.success('Invoice URL updated successfully');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const uploadFile = async (event, row) => {
    setUploadInvoiceLoader(row?.id);
    const files = event.target?.files;

    if (files && files.length > 0) {
      // Check file size (10 MB = 10 * 1024 * 1024 bytes)
      const maxSize = 10 * 1024 * 1024;
      const fileSize = files[0].size;
      if (fileSize > maxSize) {
        toast.error('File size should not exceed 10 MB.');
        setUploadInvoiceLoader(false);
        return;
      }

      try {
        const promises = Array.from(files).map((file) => {
          const formData = new FormData();
          formData.append('file', file);
          return axiosPost(
            `${process.env.REACT_APP_REST_API_ENDPOINT}common/upload-file?containerName=assets`,
            formData,
            {},
            auth,
          );
        });

        const urlParamsListDTO = await Promise.all(promises);

        const urls = urlParamsListDTO.map((res) => {
          return res?.data?.data;
        });

        if (urls?.length > 0) {
          const firstUrl = urls[0];
          await updateInoviceUrlForSubscriptionPayment(row?.id, firstUrl);
        }
      } catch (err) {
        toast.error(err.message);
      } finally {
        setUploadInvoiceLoader(false);
      }
    }
  };

  const linkFormatter = ({ row }) => {
    const hasUpdatedUrl =
      Array.isArray(updatedInvoiceUrls) &&
      updatedInvoiceUrls.find((u) => u.id === row.original.id);
    const openInvoiceUrl = () => {
      const url = hasUpdatedUrl?.url || row?.original?.invoiceUrl;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    };

    return (
      <>
        {row?.original?.invoiceUrl || hasUpdatedUrl ? (
          <>
            <Button
              outline
              color="link"
              className="text-decoration-none border-0 text-primary"
              onClick={openInvoiceUrl}
            >
              <HiOutlineDownload size={20} />
            </Button>

            <Input
              type="file"
              style={{ display: 'none' }}
              id={`upload-input-${row.original.id}`}
              onChange={(e) => uploadFile(e, row.original)}
            />

            <label
              htmlFor={`upload-input-${row.original.id}`}
              style={{ cursor: 'pointer' }}
            >
              <MdModeEdit size={20} />
            </label>
          </>
        ) : (
          <Input
            type="file"
            style={{ display: 'none' }}
            id={`upload-input-${row.original.id}`}
            onChange={(e) => uploadFile(e, row.original)}
          />
        )}
        {!row?.original?.invoiceUrl && !hasUpdatedUrl && (
          <label
            htmlFor={`upload-input-${row.original.id}`}
            style={{ cursor: 'pointer' }}
          >
            <LuUpload size={20} />
            <span className="ms-1">Upload</span>
          </label>
        )}
        {uploadInvoiceLoader && uploadInvoiceLoader === row?.original?.id && (
          <span className="ms-1">Uploading...</span>
        )}
      </>
    );
  };

  useEffect(() => {
    const billingAndPaymentColumnStateClone = [...billingAndPaymentColumnState];
    const findIndexOfItem = (item) => {
      return billingAndPaymentColumnStateClone.findIndex(
        (itm) => itm.accessorKey === item,
      );
    };
    billingAndPaymentColumnStateClone[findIndexOfItem('actions')].cell =
      linkFormatter;

    setBillingAndPaymentColumnState(billingAndPaymentColumnStateClone);
  }, [uploadInvoiceLoader, updatedInvoiceUrls]);

  if (loading) return <Loader />;
  if (error) {
    return <ErrorMessage error={error} />;
  }
  if (!data?.userSubscriptionPayments?.userSubscriptionPayments)
    return <NoDataFound message="Data not available" />;

  return (
    <ErrorBoundary>
      {showAddRecord && (
        <FSBModal
          title="PDF View"
          closeButtonLabel=""
          confirmButtonLabel=""
          message=""
          open={showAddRecord}
          onConfirmClick={drawerToggle}
          toggleAlert={drawerToggle}
          onClose={drawerToggle}
          modalClass="right-drawer right-drawer-details"
        >
          <VendorInvoiceView id={selectedInvoiceId} />
        </FSBModal>
      )}
      <FSBTable
        data={
          (data && data?.userSubscriptionPayments?.userSubscriptionPayments) ||
          []
        }
        totalCount={data?.userSubscriptionPayments?.totalCount}
        totalPages={data?.userSubscriptionPayments?.totalPages}
        columns={billingAndPaymentColumnState}
        currentPageNo={filters.pageNo}
        pageSize={filters.pageSize}
        keyField="id"
        showSearch={false}
        showFilters
        showDelete={false}
        showPagination
        remote
        striped={false}
        hover={false}
        bordered={false}
        wrapperClass="table-responsive word-break"
        handlePageChange={handlePageChange}
        //   globalSearchStr={filters.filter[0]?.value}
        //   onGlobalSearch={onGlobalSearch}
        onSortChange={onSortChange}
        sorting={sortBy}
        searchPlaceholder="Search Vendors"
      />
    </ErrorBoundary>
  );
};
export default VendorBillingAndPayment;
